import { create } from "zustand";
import calculations from "@/lib/calculations";
import { toFixedNumber } from "@/lib/utils";
import type { Insurance } from "@/types/insurance/insurance";

export interface FormulasOptions {
	currency: "NOK" | "G";
	causedBy: "accident" | "illness";
	at: "work" | "other";
	show: "compulsory" | "employer" | "full";
	insurancesToInclude?: Insurance["type"][];
}

export interface FormulasState {
	// Input state
	ageInput: string;
	annualSalaryInput: string;
	invalidityInput: string;
	disabilityInput: string;
	soleProviderInput: { label: string; value: boolean } | null;
	numOfChildrenInput: string;
	childAgesInput: string[];
	// spouseAgeInput: string;
	maritalStatusInput: {
		label: string;
		value: "married" | "partner" | "cohabitant" | "single" | null;
	} | null;

	// State
	insurances: Insurance[];
	age: number;
	annualSalary: number;
	invalidity: number;
	disability: number;
	soleProvider: boolean;
	childAges: number[];
	// spouseAge: number;
	maritalStatus: "married" | "partner" | "cohabitant" | "single" | null;
	numOfChildren: number;

	// Input methods
	setAgeInput: (age: string) => void;
	setAnnualSalaryInput: (annualSalary: string, gValue: number) => void;
	setInvalidityInput: (invalidity: string) => void;
	setDisabilityInput: (disability: string) => void;
	setSoleProviderInput: (soleProvider: {
		label: string;
		value: boolean;
	}) => void;
	setChildAgesInput: (childAges: string[]) => void;
	// setSpouseAgeInput: (spouseAge: string) => void;
	setNumOfChildrenInput: (numOfChildren: string) => void;
	setMaritalStatusInput: (newValue: {
		label: string;
		value: "married" | "partner" | "cohabitant" | "single" | null;
	}) => void;

	// Methods
	setInsurances: (newValue: Insurance[]) => void;
	setAge: (newValue: number) => void;
	setAnnualSalary: (newValue: number) => void;
	setInvalidity: (newValue: number) => void;
	setDisability: (newValue: number) => void;
	setSoleProvider: (newValue: boolean) => void;
	setChildsAges: (newValue: number[]) => void;
	// setSpouseAge: (newValue: number) => void;
	setMaritalStatus: (
		newValue: "married" | "partner" | "cohabitant" | "single" | null,
	) => void;

	// Calculations
	getInvalidityPayout: (options: FormulasOptions, gValue: number) => number;
	getDisabilityPayout: (options: FormulasOptions, gValue: number) => number;
	getDeathPayout: (options: FormulasOptions, gValue: number) => number;

	// Extra products
	hasCoordinationInsurance: () => boolean;
	hasHealthInsurance: () => boolean;
	hasTravelInsurance: () => boolean;
}

type DebugRow = {
	component: string;
	insuranceType?: Insurance["type"] | "legal";
	included: boolean;
	reason: string;
	coordinationSource?: string;
	coordinationApplied?: boolean;
	valueG: number;
};

const useFormulas = create<FormulasState>()((set, get) => ({
	// Input state
	ageInput: "45",
	annualSalaryInput: "600000",
	invalidityInput: "100",
	disabilityInput: "100",
	soleProviderInput: null,
	childAgesInput: [],
	// spouseAgeInput: "45",
	numOfChildrenInput: "",
	maritalStatusInput: null,

	// State
	insurances: [],
	age: 45,
	annualSalary: 600_000 / 130_160, // This is updated when products are retrieved to use the current G value
	invalidity: 1,
	disability: 1,
	soleProvider: false,
	childAges: [],
	// spouseAge: 45,
	maritalStatus: null,
	numOfChildren: 0,

	// Input methods
	setAgeInput: (age) => {
		const parsedAge = parseInt(age);
		set({ ageInput: age });
		if (Number.isNaN(parsedAge)) {
			console.error("Invalid value entered");
			return;
		}
		set({ age: parsedAge });
	},
	setAnnualSalaryInput: (annualSalary, gValue) => {
		console.log("Setting annual salary input", annualSalary, gValue);
		const parsedSalary = parseInt(annualSalary);
		console.log("Parsed salary", parsedSalary);
		set({ annualSalaryInput: annualSalary });
		if (Number.isNaN(parsedSalary)) {
			console.error("Invalid value entered");
			return;
		}
		set({ annualSalary: parsedSalary / gValue });
	},
	setInvalidityInput: (invalidity) => {
		if (invalidity.length > 3) return;
		const parsedInvalidity = parseFloat(invalidity);
		if (!Number.isNaN(parsedInvalidity) && parsedInvalidity > 100) {
			console.error("Invalid value entered, must be less than or equal to 100");
			return;
		}
		if (!Number.isNaN(parsedInvalidity) && parsedInvalidity < 0) {
			console.error("Invalid value entered, must be less than or equal to 100");
			return;
		}
		set({ invalidityInput: invalidity });
		if (Number.isNaN(parsedInvalidity)) {
			console.error("Invalid value entered");
			return;
		}
		set({ invalidity: parsedInvalidity / 100 });
	},
	setDisabilityInput: (disability) => {
		if (disability.length > 3) return;
		const parsedDisability = parseFloat(disability);
		if (!Number.isNaN(parsedDisability) && parsedDisability > 100) {
			console.error("Invalid value entered, must be less than or equal to 100");
			return;
		}
		if (!Number.isNaN(parsedDisability) && parsedDisability < 0) {
			console.error("Invalid value entered, must be less than or equal to 100");
			return;
		}
		set({ disabilityInput: disability });
		if (Number.isNaN(parsedDisability)) {
			console.error("Invalid value entered");
			return;
		}
		set({ disability: parsedDisability / 100 });
	},
	setSoleProviderInput: (soleProvider) => {
		set({ soleProviderInput: soleProvider, soleProvider: soleProvider.value });
	},
	setChildAgesInput: (childAges) => {
		const parsedChildAges = childAges.map((age) => parseInt(age));
		set({ childAgesInput: childAges });
		if (parsedChildAges.some(Number.isNaN)) {
			console.error("Invalid value entered");
			return;
		}
		set({ childAges: parsedChildAges });
	},
	// setSpouseAgeInput: (spouseAge) => {
	// 	const parsedSpouseAge = parseInt(spouseAge);
	// 	set({ spouseAgeInput: spouseAge });
	// 	if (isNaN(parsedSpouseAge)) {
	// 		console.error("Invalid value entered");
	// 		return;
	// 	}
	// 	set({ spouseAge: parsedSpouseAge });
	// },
	setMaritalStatusInput: (newValue) => {
		set({ maritalStatusInput: newValue });
		set({ maritalStatus: newValue.value });
	},

	setNumOfChildrenInput: (numOfChildren) => {
		const parsedNumOfChildren = parseFloat(numOfChildren);
		if (!Number.isNaN(parsedNumOfChildren) && parsedNumOfChildren > 8) {
			console.error("Invalid value entered, must be less than or equal to 8");
			return;
		}
		if (!Number.isNaN(parsedNumOfChildren) && parsedNumOfChildren < 0) {
			console.error("Invalid value entered, must be less than or equal to 0");
			return;
		}
		set({ numOfChildrenInput: numOfChildren });
		if (Number.isNaN(parsedNumOfChildren)) {
			console.error("Invalid value entered");
			return;
		}
		set({ numOfChildren: parsedNumOfChildren });

		const childAges = Array.from(
			{ length: parsedNumOfChildren },
			(_, i) => (i + 1) * 2,
		);
		const state = get();
		state.setChildAgesInput(childAges.map((age) => age.toString()));
	},

	// Methods
	setInsurances: (newValue) => {
		set({ insurances: newValue });
	},
	setAge: (newValue) => {
		set({ age: newValue });
	},
	setAnnualSalary: (newValue) => {
		set({ annualSalary: newValue });
	},
	setInvalidity: (newValue) => {
		set({ invalidity: newValue });
	},
	setDisability: (newValue) => {
		set({ disability: newValue });
	},
	setSoleProvider: (newValue) => {
		set({ soleProvider: newValue });
	},
	setChildsAges: (newValue) => {
		set({ childAges: newValue });
	},
	// setSpouseAge: (newValue) => {
	// 	set({ spouseAge: newValue });
	// },
	setMaritalStatus: (newValue) => {
		set({ maritalStatus: newValue });
	},

	// Calculations
	getInvalidityPayout: (options, gValue) => {
		const state = get();
		const { insurances, age, invalidity } = state;

		if (!state.insurances || state.insurances.length === 0) {
			// console.error("No insurances found");
			return 0;
		}

		const filter = options.insurancesToInclude;
		const collectiveOnlyFilter =
			filter?.length === 1 && filter[0] === "collective-accident-invalidity";
		const leisureOnlyFilter =
			filter?.length === 1 && filter[0] === "leisure-accident-invalidity";
		const trygghetsOnlyFilter =
			filter?.length === 1 && filter[0] === "trygghetsforsikring";

		if (collectiveOnlyFilter) {
			if (options.causedBy !== "accident") {
				return 0;
			}
			const collectiveIns = insurances.find(
				(i) => i.type === "collective-accident-invalidity",
			);
			const collectivePayout =
				calculations.collectiveAccidentInvalidityCompensation(
					collectiveIns,
					age,
					invalidity,
				);
			const scaleMultiplier = options.currency === "NOK" ? gValue : 1;
			switch (options.show) {
				case "compulsory":
					return 0;
				case "employer":
				case "full":
					return collectivePayout * scaleMultiplier;
				default:
					return 0;
			}
		}

		if (leisureOnlyFilter || trygghetsOnlyFilter) {
			if (options.at !== "other" || options.causedBy !== "accident") {
				return 0;
			}
		}

		const shouldAggregateCollective =
			(filter == null || filter.length === 0) &&
			options.causedBy === "accident";
		const collectiveInsurance = insurances.find(
			(i) => i.type === "collective-accident-invalidity",
		);
		const collectiveAdd = shouldAggregateCollective
			? calculations.collectiveAccidentInvalidityCompensation(
					collectiveInsurance,
					age,
					invalidity,
				)
			: 0;

		const getInsuranceType = () => {
			if (options.causedBy === "accident" && options.at === "work") {
				return "extended-wc-accident";
			}
			if (options.causedBy === "accident" && options.at === "other") {
				const onlyLeisureInvalidity =
					filter?.length === 1 &&
					filter[0] === "leisure-accident-invalidity";
				const hasTrygghetsforsikring = insurances.some(
					(i) => i.type === "trygghetsforsikring",
				);
				if (hasTrygghetsforsikring && !onlyLeisureInvalidity) {
					return "trygghetsforsikring";
				}
				return "leisure-accident-invalidity";
			}
			if (options.causedBy === "illness" && options.at === "work") {
				return "extended-wc-illness";
			}
			return "other-illness-invalidity";
		};

		const getMinimumInvalidity = (): number => {
			// Try to get min-invalidity from the insurance parameters
			const minInvalidityParam = insurance?.params?.find(
				(param) => param.type === "min-invalidity",
			)?.value;

			if (minInvalidityParam !== undefined) {
				// true = covers 1-15% invalidity (minimum 1%)
				// false = standard minimum 15%
				return minInvalidityParam === true ? 0.01 : 0.15;
			}

			// Fallback to hardcoded values if parameter not found
			if (options.causedBy === "illness" && options.at === "other") {
				return 0.5;
			}
			return 0.01;
		};

		const getMinimumInvalidityForWC = (): number => {
			const wcInsurance = insurances.find((i) => i.type === "wc");
			if (!wcInsurance) return 0.15; // Default if no WC insurance found

			const minInvalidityParam = wcInsurance.params?.find(
				(param) => param.type === "min-invalidity",
			)?.value;

			if (minInvalidityParam !== undefined) {
				// true = covers 1-15% invalidity (minimum 1%)
				// false = standard minimum 15%
				return minInvalidityParam === true ? 0.01 : 0.15;
			}

			// Default to 15% if parameter not found
			return 0.15;
		};

		const insuranceType = getInsuranceType();
		const insurance = insurances.find((i) => i.type === insuranceType);
		const minimumInvalidity = getMinimumInvalidity();

		const additionalPayout =
			calculations.additionalMedicalDisabilityCompensation(
				insurance,
				age,
				invalidity,
				minimumInvalidity,
			);

		const hasCompulsoryInsurance = insurances.find((i) => i.type === "wc");
		const legalPayout = hasCompulsoryInsurance
			? calculations.legalMedicalDisabilityCompensation(
					age,
					invalidity,
					getMinimumInvalidityForWC(),
				)
			: 0;

		const scaleMultiplier = options.currency === "NOK" ? gValue : 1;

		const debugRows: DebugRow[] = [
			{
				component: "Lovpålagt medisinsk invaliditet",
				insuranceType: "legal",
				included: options.at === "work",
				reason:
					options.at === "work"
						? "Lovpålagt dekning gjelder ved arbeid"
						: "Ikke ved fritid/annet",
				valueG: legalPayout,
			},
			{
				component: "Utvidet/annen medisinsk invaliditet",
				insuranceType: insuranceType,
				included: additionalPayout > 0,
				reason:
					additionalPayout > 0
						? "Dekning funnet og beregnet"
						: "Ingen relevant forsikring eller under minste invaliditet",
				valueG: additionalPayout,
			},
			{
				component: "Kollektiv ulykke invaliditet",
				insuranceType: "collective-accident-invalidity",
				included: collectiveAdd > 0,
				reason:
					collectiveAdd > 0
						? "Inkludert i aggregering for ulykke"
						: "Ikke aktuell gitt filter/årsak",
				valueG: collectiveAdd,
			},
		];

		const debugResult = (result: number) => {
			logFormulaDebug("invalidity", {
				options,
				gValue,
				inputState: {
					age: state.age,
					invalidity: state.invalidity,
					annualSalary: state.annualSalary,
					insurancesToInclude: options.insurancesToInclude ?? [],
				},
				rows: debugRows,
				totals: {
					scaleMultiplier,
					resultG: options.currency === "G" ? result : result / gValue,
					resultNOK: options.currency === "NOK" ? result : result * gValue,
				},
			});
			return result;
		};

		switch (options.show) {
			case "compulsory":
				return debugResult(
					options.at === "work" ? legalPayout * scaleMultiplier : 0,
				);

			case "employer":
				// When expanded insurance exists, show the difference so total = expanded amount
				// (compulsory shows legalPayout, employer shows additionalPayout - legalPayout).
				// Collective accident invalidity is 24h cover and must still be added
				// (HOW-102); otherwise calculator employer+compulsory misses it while
				// show:"full" (matrix sum) includes it.
				if (options.at === "work" && additionalPayout > 0) {
					const employerPortion = additionalPayout - legalPayout;
					return debugResult(
						(Math.max(0, employerPortion) + collectiveAdd) * scaleMultiplier,
					);
				}
				return debugResult((additionalPayout + collectiveAdd) * scaleMultiplier);

			case "full": {
				// extended coverage (Utvidet yrkesskade) overwrites the legal minimum (Lovpålagt yrkesskade) if present.
				const workAccidentTotal =
					additionalPayout > 0 ? additionalPayout : legalPayout;
				// Collective accident invalidity is 24h cover, so it also counts
				// towards the work accident column (HOW-102).
				return debugResult(
					options.at === "work"
						? parseFloat(
								((workAccidentTotal + collectiveAdd) * scaleMultiplier).toFixed(2),
							)
						: (additionalPayout + collectiveAdd) * scaleMultiplier,
				);
			}
			default:
				return debugResult(0);
		}
	},
	getDisabilityPayout: (options, gValue) => {
		const state = get();
		if (!state.insurances || state.insurances.length === 0) {
			// console.error("No insurances found");
			return 0;
		}
		let additionalPayout: number = 0;

		if (options.causedBy === "accident") {
			if (options.at === "other") {
				const insurance = state.insurances.find(
					(insurance) => insurance.type === "leisure-accident-disability",
				);

				additionalPayout =
					calculations.additionalOccupationalDisabilityCompensation(
						insurance,
						state.age,
						state.annualSalary,
						state.disability,
					);
			}
		} else {
			// Illness
			if (options.at === "other") {
				const insurance = state.insurances.find(
					(insurance) => insurance.type === "other-illness-disability",
				);

				additionalPayout =
					calculations.additionalOccupationalDisabilityCompensation(
						insurance,
						state.age,
						state.annualSalary,
						state.disability,
						0.4,
					);
			}
		}

		const insurance = state.insurances.find(
			(insurance) => insurance.type === "disability-capital",
		);

		if (
			insurance &&
			isIncludedInFilter("disability-capital", options.insurancesToInclude)
		) {
			additionalPayout += calculations.disabilityCapitalCompensation(
				insurance,
				state.age,
			);
		}

		const legalPayout = calculations.legalOccupationalDisabilityCompensation(
			state.age,
			state.annualSalary,
			state.disability,
		);

		const scaleMultiplier = options.currency === "NOK" ? gValue : 1;

		if (options.show === "compulsory" && options.at === "work") {
			return legalPayout * scaleMultiplier;
		}

		if (options.show === "employer") {
			return additionalPayout * scaleMultiplier;
		}

		if (options.show === "full") {
			if (options.at === "work") {
				return parseFloat(
					((legalPayout + additionalPayout) * scaleMultiplier).toFixed(2),
				);
			} else {
				return additionalPayout * scaleMultiplier;
			}
		}

		return 0;
	},
	getDeathPayout: (options, gValue) => {
		const state = get();
		if (!state.insurances || state.insurances.length === 0) {
			return 0;
		}
		let additionalPayout: number = 0;

		// Calculate leisure accident death components separately for coordination
		let leisureAccidentDeathBasePayout = 0;
		let leisureAccidentDeathChildSupplementPayout = 0;
		let leisureAccidentDeathFuneralAllowancePayout = 0;
		const accidentDeathOtherInsurance = resolveAccidentDeathOtherInsurance(
			state.insurances,
			options.insurancesToInclude,
		);

		if (options.causedBy === "accident" && options.at === "other") {
			if (accidentDeathOtherInsurance) {
				leisureAccidentDeathBasePayout =
					calculations.leisureAccidentDeathBaseCoverage(
						accidentDeathOtherInsurance,
						state.age,
						state.maritalStatus,
						state.childAges,
					);

				leisureAccidentDeathChildSupplementPayout =
					calculations.leisureAccidentDeathChildSupplement(
						accidentDeathOtherInsurance,
						state.childAges,
						state.soleProvider,
					);

				leisureAccidentDeathFuneralAllowancePayout =
					calculations.leisureAccidentDeathFuneralAllowance(
						accidentDeathOtherInsurance,
					);
			}
		}

		const baseCoverageInsurance = state.insurances.find(
			(insurance) => insurance.type === "group-life-base-coverage",
		);

		const hasCoordination = state.hasCoordinationInsurance();

		let groupLifeBaseCoveragePayout = 0;
		if (
			baseCoverageInsurance &&
			isIncludedInFilter(
				"group-life-base-coverage",
				options.insurancesToInclude,
			)
		) {
			groupLifeBaseCoveragePayout = calculations.groupLifeBaseCoverage(
				baseCoverageInsurance,
				state.age,
			);
		}

		// Calculate group life child supplement separately for coordination logic
		const childSupplementInsurance = state.insurances.find(
			(insurance) => insurance.type === "group-life-child-supplement",
		);

		let groupLifeChildSupplementPayout = 0;
		if (
			childSupplementInsurance &&
			isIncludedInFilter(
				"group-life-child-supplement",
				options.insurancesToInclude,
			)
		) {
			groupLifeChildSupplementPayout = calculations.groupLifeChildSupplement(
				childSupplementInsurance,
				state.childAges,
				state.soleProvider,
			);
		}

		// Child supplement (barnetillegg) coordination is controlled independently
		// of the overall group life coordination (HOW-97). When the group life
		// child supplement carries an explicit `child-supplement-coordination`
		// flag we honour it; otherwise we fall back to the global coordination
		// setting so existing agreements keep their previous behaviour.
		const childSupplementCoordinationParam =
			childSupplementInsurance?.params?.find(
				(param) => param.type === "child-supplement-coordination",
			)?.value;
		const childSupplementCoordination =
			typeof childSupplementCoordinationParam === "boolean"
				? childSupplementCoordinationParam
				: hasCoordination;

		const annualSalaryInsurance = state.insurances.find(
			(insurance) => insurance.type === "group-life-annual-salary",
		);

		if (
			annualSalaryInsurance &&
			isIncludedInFilter(
				"group-life-annual-salary",
				options.insurancesToInclude,
			)
		) {
			additionalPayout += calculations.groupLifeAnnualSalary(
				annualSalaryInsurance,
				state.annualSalary,
				state.age,
			);
		}

		const gradualReductionInsurance = state.insurances.find(
			(insurance) => insurance.type === "group-life-gradual-reduction",
		);

		if (
			gradualReductionInsurance &&
			isIncludedInFilter(
				"group-life-gradual-reduction",
				options.insurancesToInclude,
			)
		) {
			additionalPayout += calculations.groupLifeGradualReduction(
				gradualReductionInsurance,
				state.age,
				state.annualSalary,
			);
		}

		const annualSalaryAboveXGInsurance = state.insurances.find(
			(insurance) => insurance.type === "group-life-annual-salary-above-x-g",
		);
		if (
			annualSalaryAboveXGInsurance &&
			isIncludedInFilter(
				"group-life-annual-salary-above-x-g",
				options.insurancesToInclude,
			)
		) {
			additionalPayout += calculations.groupLifeAnnualSalaryAboveXG(
				annualSalaryAboveXGInsurance,
				state.annualSalary,
				state.age,
			);
		}

		const intervalsInsurance = state.insurances.find(
			(insurance) => insurance.type === "group-life-intervals",
		);

		if (
			intervalsInsurance &&
			isIncludedInFilter("group-life-intervals", options.insurancesToInclude)
		) {
			additionalPayout += calculations.groupLifeIntervals(
				intervalsInsurance,
				state.age,
				state.annualSalary,
			);
		}

		const htaInsurance = state.insurances.find(
			(insurance) => insurance.type === "group-life-hta",
		);

		let htaPayout = 0;
		if (
			htaInsurance &&
			isIncludedInFilter("group-life-hta", options.insurancesToInclude)
		) {
			htaPayout = calculations.groupLifeHTA(
				htaInsurance,
				state.age,
				state.maritalStatus,
				state.childAges,
			);
			additionalPayout += htaPayout;
		}

		const dependentsAllowanceBaseCoverageInsurance = state.insurances.find(
			(insurance) => insurance.type === "dependents-allowance-base-coverage",
		);

		if (
			dependentsAllowanceBaseCoverageInsurance &&
			isIncludedInFilter(
				"dependents-allowance-base-coverage",
				options.insurancesToInclude,
			)
		) {
			additionalPayout += calculations.dependentsAllowanceBaseCoverage(
				dependentsAllowanceBaseCoverageInsurance,
				state.childAges,
				state.maritalStatus,
				state.age,
			);
		}

		const dependentsAllowanceAnnualSalaryInsurance = state.insurances.find(
			(insurance) => insurance.type === "dependents-allowance-annual-salary",
		);

		if (
			dependentsAllowanceAnnualSalaryInsurance &&
			isIncludedInFilter(
				"dependents-allowance-annual-salary",
				options.insurancesToInclude,
			)
		) {
			additionalPayout += calculations.dependentsAllowanceAnnualSalary(
				dependentsAllowanceAnnualSalaryInsurance,
				state.annualSalary,
				state.childAges,
				state.maritalStatus,
				state.age,
			);
		}
		// Keep group life and related add-ons before mixing in leisure base coverage.
		const groupLifeAdditionalPayout = additionalPayout;

		const legalBasePayout = calculations.legalBaseLifeInsuranceCompensation(
			state.age,
			state.maritalStatus,
		);

		const legalChildPayout =
			calculations.legalChildSupplementLifeInsuranceCompensation(
				state.childAges,
				state.soleProvider,
			);

		const legalFuneralAllowancePayout = calculations.legalFuneralAllowance(
			state.age,
		);

		const legalPayout =
			legalBasePayout + legalChildPayout + legalFuneralAllowancePayout;

		// Apply coordination to child supplements if coordination is enabled for
		// the child supplement (controlled independently from the rest of the
		// group life agreement, see HOW-97).
		// Coordinate between legal WC, leisure accident death, and group life child supplements
		const coordinatedChildSupplement = childSupplementCoordination
			? Math.max(
					legalChildPayout,
					leisureAccidentDeathChildSupplementPayout,
					groupLifeChildSupplementPayout,
				)
			: legalChildPayout +
				leisureAccidentDeathChildSupplementPayout +
				groupLifeChildSupplementPayout;

		// Calculate employer contribution for child supplement with coordination
		// When coordinated, employer pays the max minus legal (which is compulsory)
		const employerChildSupplementContribution = childSupplementCoordination
			? Math.max(
					0,
					Math.max(
						leisureAccidentDeathChildSupplementPayout,
						groupLifeChildSupplementPayout,
					) - legalChildPayout,
				)
			: leisureAccidentDeathChildSupplementPayout +
				groupLifeChildSupplementPayout;

		// For "other" (spare time) scenarios, coordinate just between leisure and group life (no legal)
		const coordinatedOtherChildSupplement = childSupplementCoordination
			? Math.max(
					leisureAccidentDeathChildSupplementPayout,
					groupLifeChildSupplementPayout,
				)
			: leisureAccidentDeathChildSupplementPayout +
				groupLifeChildSupplementPayout;

		// Apply coordination to base coverages if coordination insurance exists
		// Coordinate between leisure accident death base and group life base coverage
		const coordinatedOtherBaseCoverage = hasCoordination
			? Math.max(leisureAccidentDeathBasePayout, groupLifeBaseCoveragePayout)
			: leisureAccidentDeathBasePayout + groupLifeBaseCoveragePayout;

		// For "other" (spare time), add the coordinated base to additionalPayout
		// (additionalPayout already contains other coverages like annual salary, HTA, etc.)
		additionalPayout += coordinatedOtherBaseCoverage;

		// Apply coordination to funeral allowances if coordination insurance exists
		// Coordinate between legal WC, leisure accident death, and group life funeral allowances
		const coordinatedFuneralAllowance = hasCoordination
			? Math.max(
					legalFuneralAllowancePayout,
					leisureAccidentDeathFuneralAllowancePayout,
				)
			: legalFuneralAllowancePayout +
				leisureAccidentDeathFuneralAllowancePayout;

		// Calculate employer contribution for funeral allowance with coordination
		// When coordinated, employer pays the max minus legal (which is compulsory)
		const employerFuneralAllowanceContribution = hasCoordination
			? Math.max(
					0,
					leisureAccidentDeathFuneralAllowancePayout -
						legalFuneralAllowancePayout,
				)
			: leisureAccidentDeathFuneralAllowancePayout;

		// For "other" (spare time) scenarios, funeral allowance is just from leisure accident
		const coordinatedOtherFuneralAllowance =
			leisureAccidentDeathFuneralAllowancePayout;

		const collectiveDeathInsurance = state.insurances.find(
			(insurance) => insurance.type === "collective-accident-death",
		);
		// Flat G; not coordinated with group life / HTA caps
		const collectiveDeathPayout =
			options.causedBy === "accident" &&
			collectiveDeathInsurance &&
			isIncludedInFilter(
				"collective-accident-death",
				options.insurancesToInclude,
			)
				? calculations.collectiveAccidentDeathCompensation(
						collectiveDeathInsurance,
						state.age,
					)
				: 0;
		const workAccidentGroupLifeEmployerPayout =
			additionalPayout +
			employerChildSupplementContribution +
			employerFuneralAllowanceContribution;
		const workAccidentEmployerDeathPayout =
			workAccidentGroupLifeEmployerPayout + collectiveDeathPayout;

		const scaleMultiplier = options.currency === "NOK" ? gValue : 1;
		const coordinationAmount = htaInsurance?.params?.find(
			(param) => param.type === "coordination-amount",
		)?.value;
		const coordinationAmountValue = coordinationAmount
			? parseFloat(coordinationAmount as string)
			: null;
		const hasCoordinationAmountCap =
			coordinationAmountValue != null &&
			!Number.isNaN(coordinationAmountValue) &&
			coordinationAmountValue > 0;
		const coordinatedWorkBasePayout = hasCoordinationAmountCap
			? Math.min(legalBasePayout + additionalPayout, coordinationAmountValue)
			: additionalPayout;
		const coordinatedWorkEmployerPayout =
			Math.max(0, coordinatedWorkBasePayout - legalBasePayout) +
			employerChildSupplementContribution +
			employerFuneralAllowanceContribution +
			collectiveDeathPayout;
		const coordinatedWorkFullPayout =
			coordinatedWorkBasePayout +
			coordinatedChildSupplement +
			coordinatedFuneralAllowance +
			collectiveDeathPayout;
		const debugRows: DebugRow[] = [
			{
				component: "Lovpålagt grunnsum",
				insuranceType: "legal",
				included: options.at === "work",
				reason:
					options.at === "work"
						? "Lovpålagt utbetaling ved arbeid"
						: "Gjelder ikke for fritid",
				valueG: legalBasePayout,
			},
			{
				component: "Lovpålagt barnetillegg",
				insuranceType: "legal",
				included: options.at === "work",
				reason:
					options.at === "work"
						? "Lovpålagt barnetillegg ved arbeid"
						: "Ikke lovpålagt i fritidssum",
				coordinationSource: childSupplementCoordination
					? "child-supplement-coordination/global coordination"
					: "ingen",
				coordinationApplied: childSupplementCoordination,
				valueG: legalChildPayout,
			},
			{
				component: "Lovpålagt gravferd",
				insuranceType: "legal",
				included: options.at === "work",
				reason:
					options.at === "work"
						? "Lovpålagt gravferdsstøtte ved arbeid"
						: "Ikke lovpålagt i fritidssum",
				coordinationSource: hasCoordination ? "group-life-coordination" : "ingen",
				coordinationApplied: hasCoordination,
				valueG: legalFuneralAllowancePayout,
			},
			{
				component: "Fritidsulykke/trygghets grunnsum",
				insuranceType: accidentDeathOtherInsurance?.type,
				included: leisureAccidentDeathBasePayout > 0,
				reason:
					accidentDeathOtherInsurance?.type != null
						? "Dekning valgt via resolveAccidentDeathOtherInsurance"
						: "Ingen relevant fritidsdekning valgt",
				coordinationSource: hasCoordination ? "group-life-coordination" : "ingen",
				coordinationApplied: hasCoordination,
				valueG: leisureAccidentDeathBasePayout,
			},
			{
				component: "Gruppeliv grunnsum",
				insuranceType: "group-life-base-coverage",
				included: groupLifeBaseCoveragePayout > 0,
				reason:
					groupLifeBaseCoveragePayout > 0
						? "Inkludert i filter og beregnet"
						: "Mangler dekning eller filtrert bort",
				coordinationSource: hasCoordination ? "group-life-coordination" : "ingen",
				coordinationApplied: hasCoordination,
				valueG: groupLifeBaseCoveragePayout,
			},
			{
				component: "Fritidsulykke/trygghets barnetillegg",
				insuranceType: accidentDeathOtherInsurance?.type,
				included: leisureAccidentDeathChildSupplementPayout > 0,
				reason:
					leisureAccidentDeathChildSupplementPayout > 0
						? "Barnetillegg beregnet fra fritidsulykke/trygghet"
						: "Ingen fritid barnetillegg",
				coordinationSource: childSupplementCoordination
					? "child-supplement-coordination/global coordination"
					: "ingen",
				coordinationApplied: childSupplementCoordination,
				valueG: leisureAccidentDeathChildSupplementPayout,
			},
			{
				component: "Gruppeliv barnetillegg",
				insuranceType: "group-life-child-supplement",
				included: groupLifeChildSupplementPayout > 0,
				reason:
					groupLifeChildSupplementPayout > 0
						? "Barnetillegg beregnet fra gruppeliv"
						: "Mangler dekning eller filtrert bort",
				coordinationSource: childSupplementCoordination
					? "child-supplement-coordination/global coordination"
					: "ingen",
				coordinationApplied: childSupplementCoordination,
				valueG: groupLifeChildSupplementPayout,
			},
			{
				component: "Fritidsulykke/trygghets gravferd",
				insuranceType: accidentDeathOtherInsurance?.type,
				included: leisureAccidentDeathFuneralAllowancePayout > 0,
				reason:
					leisureAccidentDeathFuneralAllowancePayout > 0
						? "Gravferd beregnet fra fritidsulykke/trygghet"
						: "Ingen gravferdsytelse fra fritidsulykke/trygghet",
				coordinationSource: hasCoordination ? "group-life-coordination" : "ingen",
				coordinationApplied: hasCoordination,
				valueG: leisureAccidentDeathFuneralAllowancePayout,
			},
			{
				component: "Oevrige arbeidsgiverdekninger",
				insuranceType: "group-life-hta",
				included: groupLifeAdditionalPayout > 0,
				reason: "Summen av aarlig loenn/HTA/intervaller/forsoergertillegg",
				coordinationSource: hasCoordinationAmountCap
					? "hta coordination-amount"
					: "ingen",
				coordinationApplied: hasCoordinationAmountCap,
				valueG: groupLifeAdditionalPayout,
			},
			{
				component: "Kollektiv ulykke doed",
				insuranceType: "collective-accident-death",
				included: collectiveDeathPayout > 0,
				reason:
					collectiveDeathPayout > 0
						? "Flat G-utbetaling lagt paa toppen"
						: "Ingen kollektiv ulykkesdoed i scenario/filter",
				valueG: collectiveDeathPayout,
			},
		];
		const debugResult = (
			result: number,
			formula?: {
				branch: string;
				terms: { name: string; valueG: number }[];
			},
		) => {
			logFormulaDebug("death", {
				options,
				gValue,
				inputState: {
					age: state.age,
					annualSalary: state.annualSalary,
					maritalStatus: state.maritalStatus,
					childAges: state.childAges,
					soleProvider: state.soleProvider,
					hasCoordination,
					childSupplementCoordination,
					coordinationAmountValue,
					insurancesToInclude: options.insurancesToInclude ?? [],
				},
				rows: debugRows,
				totals: {
					legalPayout,
					groupLifeAdditionalPayout,
					coordinatedOtherBaseCoverage,
					coordinatedChildSupplement,
					coordinatedFuneralAllowance,
					workAccidentEmployerDeathPayout,
					scaleMultiplier,
					resultG: options.currency === "G" ? result : result / gValue,
					resultNOK: options.currency === "NOK" ? result : result * gValue,
				},
				formula,
			});
			return result;
		};

		if (options.at === "work" && options.causedBy === "accident") {
			if (options.show === "compulsory") {
				return debugResult(toFixedNumber(legalPayout * scaleMultiplier), {
					branch: "work-accident-compulsory",
					terms: [{ name: "legalPayout", valueG: legalPayout }],
				});
			}
			if (hasCoordination) {
				if (options.show === "employer") {
					return debugResult(
						toFixedNumber(coordinatedWorkEmployerPayout * scaleMultiplier),
						{
							branch: "work-accident-employer-coordinated",
							terms: [
								{
									name: "coordinatedWorkBaseMinusLegalBase",
									valueG: Math.max(0, coordinatedWorkBasePayout - legalBasePayout),
								},
								{
									name: "employerChildSupplementContribution",
									valueG: employerChildSupplementContribution,
								},
								{
									name: "employerFuneralAllowanceContribution",
									valueG: employerFuneralAllowanceContribution,
								},
								{
									name: "collectiveDeathPayout",
									valueG: collectiveDeathPayout,
								},
							],
						},
					);
				}
				if (options.show === "full") {
					return debugResult(
						toFixedNumber(coordinatedWorkFullPayout * scaleMultiplier),
						{
							branch: "work-accident-full-coordinated",
							terms: [
								{
									name: "coordinatedWorkBasePayout",
									valueG: coordinatedWorkBasePayout,
								},
								{
									name: "coordinatedChildSupplement",
									valueG: coordinatedChildSupplement,
								},
								{
									name: "coordinatedFuneralAllowance",
									valueG: coordinatedFuneralAllowance,
								},
								{
									name: "collectiveDeathPayout",
									valueG: collectiveDeathPayout,
								},
							],
						},
					);
				}
			}
			if (options.show === "employer") {
				return debugResult(
					toFixedNumber(workAccidentEmployerDeathPayout * scaleMultiplier),
					{
						branch: "work-accident-employer",
						terms: [
							{
								name: "workAccidentEmployerDeathPayout",
								valueG: workAccidentEmployerDeathPayout,
							},
						],
					},
				);
			}
			if (options.show === "full") {
				return debugResult(
					toFixedNumber(
					(legalPayout + workAccidentEmployerDeathPayout) * scaleMultiplier,
					),
					{
						branch: "work-accident-full",
						terms: [
							{ name: "legalPayout", valueG: legalPayout },
							{
								name: "workAccidentEmployerDeathPayout",
								valueG: workAccidentEmployerDeathPayout,
							},
						],
					},
				);
			}
		}

		if (options.at === "work" && options.causedBy === "illness") {
			if (hasCoordination) {
				// Get coordination-amount from HTA insurance if it exists
				const coordinationAmount = htaInsurance?.params?.find(
					(param) => param.type === "coordination-amount",
				)?.value;
				const coordinationAmountValue = coordinationAmount
					? parseFloat(coordinationAmount as string)
					: null;

				let coordinatedBasePayout = additionalPayout;

				// Apply coordination-amount cap if it exists
				if (
					coordinationAmountValue &&
					!Number.isNaN(coordinationAmountValue) &&
					coordinationAmountValue > 0
				) {
					// Cap only the base coverage (legal base + additional) at coordination-amount
					// Exclude child supplements and funeral allowances from the cap
					coordinatedBasePayout = Math.min(
						legalBasePayout + additionalPayout,
						coordinationAmountValue,
					);
				}

				let difference = coordinatedBasePayout - legalBasePayout;
				if (difference < 0) {
					difference = 0;
				}

				if (options.show === "compulsory") {
					return debugResult(
						toFixedNumber(
						(legalBasePayout + legalChildPayout + legalFuneralAllowancePayout) *
							scaleMultiplier,
						),
						{
							branch: "work-illness-compulsory-coordinated",
							terms: [
								{ name: "legalBasePayout", valueG: legalBasePayout },
								{ name: "legalChildPayout", valueG: legalChildPayout },
								{
									name: "legalFuneralAllowancePayout",
									valueG: legalFuneralAllowancePayout,
								},
							],
						},
					);
				} else if (options.show === "employer") {
					return debugResult(
						toFixedNumber(
						(difference +
							employerChildSupplementContribution +
							employerFuneralAllowanceContribution) *
							scaleMultiplier,
						),
						{
							branch: "work-illness-employer-coordinated",
							terms: [
								{ name: "coordinatedBaseMinusLegalBase", valueG: difference },
								{
									name: "employerChildSupplementContribution",
									valueG: employerChildSupplementContribution,
								},
								{
									name: "employerFuneralAllowanceContribution",
									valueG: employerFuneralAllowanceContribution,
								},
							],
						},
					);
				} else if (options.show === "full") {
					return debugResult(
						toFixedNumber(
						(coordinatedBasePayout +
							coordinatedChildSupplement +
							coordinatedFuneralAllowance) *
							scaleMultiplier,
						),
						{
							branch: "work-illness-full-coordinated",
							terms: [
								{ name: "coordinatedBasePayout", valueG: coordinatedBasePayout },
								{
									name: "coordinatedChildSupplement",
									valueG: coordinatedChildSupplement,
								},
								{
									name: "coordinatedFuneralAllowance",
									valueG: coordinatedFuneralAllowance,
								},
							],
						},
					);
				}
			} else {
				if (options.show === "compulsory") {
					return debugResult(toFixedNumber(legalPayout * scaleMultiplier));
				} else if (options.show === "employer") {
					return debugResult(
						toFixedNumber(
						(additionalPayout +
							employerChildSupplementContribution +
							employerFuneralAllowanceContribution) *
							scaleMultiplier,
						),
					);
				} else if (options.show === "full") {
					return debugResult(
						toFixedNumber((legalPayout + additionalPayout) * scaleMultiplier),
					);
				}
			}
		}

		if (options.show === "compulsory" && options.at === "work") {
			return debugResult(parseFloat((legalPayout * scaleMultiplier).toFixed(2)));
		}

		if (options.show === "employer" && options.at === "other") {
			// Apply coordination-amount cap for leisure scenarios with coordination
			if (hasCoordination && htaInsurance) {
				const coordinationAmount = htaInsurance.params?.find(
					(param) => param.type === "coordination-amount",
				)?.value;
				const coordinationAmountValue = coordinationAmount
					? parseFloat(coordinationAmount as string)
					: null;

				if (
					coordinationAmountValue &&
					!Number.isNaN(coordinationAmountValue) &&
					coordinationAmountValue > 0
				) {
					// Cap the total payout at coordination-amount
					const cappedTotalPayout = Math.min(
						additionalPayout +
							coordinatedOtherChildSupplement +
							coordinatedOtherFuneralAllowance,
						coordinationAmountValue,
					);
					return debugResult(
						parseFloat(
							(
								(cappedTotalPayout + collectiveDeathPayout) *
								scaleMultiplier
							).toFixed(2),
						),
						{
							branch: "other-employer-capped",
							terms: [
								{ name: "cappedTotalPayout", valueG: cappedTotalPayout },
								{ name: "collectiveDeathPayout", valueG: collectiveDeathPayout },
							],
						},
					);
				}
			}
			return debugResult(
				parseFloat(
					(
						(additionalPayout +
							coordinatedOtherChildSupplement +
							coordinatedOtherFuneralAllowance +
							collectiveDeathPayout) *
						scaleMultiplier
					).toFixed(2),
				),
				{
					branch: "other-employer-uncapped",
					terms: [
						{ name: "additionalPayout", valueG: additionalPayout },
						{
							name: "coordinatedOtherChildSupplement",
							valueG: coordinatedOtherChildSupplement,
						},
						{
							name: "coordinatedOtherFuneralAllowance",
							valueG: coordinatedOtherFuneralAllowance,
						},
						{ name: "collectiveDeathPayout", valueG: collectiveDeathPayout },
					],
				},
			);
		}

		if (options.show === "full") {
			if (options.at === "work") {
				return debugResult(
					parseFloat(((legalPayout + additionalPayout) * scaleMultiplier).toFixed(2)),
				);
			} else {
				// Apply coordination-amount cap for leisure scenarios with coordination
				if (hasCoordination && htaInsurance) {
					const coordinationAmount = htaInsurance.params?.find(
						(param) => param.type === "coordination-amount",
					)?.value;
					const coordinationAmountValue = coordinationAmount
						? parseFloat(coordinationAmount as string)
						: null;

					if (
						coordinationAmountValue &&
						!Number.isNaN(coordinationAmountValue) &&
						coordinationAmountValue > 0
					) {
						// Cap the total payout at coordination-amount
						// additionalPayout already contains the coordinated base coverage
						const cappedTotalPayout = Math.min(
							additionalPayout +
								coordinatedOtherChildSupplement +
								coordinatedOtherFuneralAllowance,
							coordinationAmountValue,
						);
						return debugResult(
							parseFloat(
								(
									(cappedTotalPayout + collectiveDeathPayout) *
									scaleMultiplier
								).toFixed(2),
							),
							{
								branch: "other-full-capped",
								terms: [
									{ name: "cappedTotalPayout", valueG: cappedTotalPayout },
									{
										name: "collectiveDeathPayout",
										valueG: collectiveDeathPayout,
									},
								],
							},
						);
					}
				}
				return debugResult(
					parseFloat(
						(
							(additionalPayout +
								coordinatedOtherChildSupplement +
								coordinatedOtherFuneralAllowance +
								collectiveDeathPayout) *
							scaleMultiplier
						).toFixed(2),
					),
					{
						branch: "other-full-uncapped",
						terms: [
							{ name: "additionalPayout", valueG: additionalPayout },
							{
								name: "coordinatedOtherChildSupplement",
								valueG: coordinatedOtherChildSupplement,
							},
							{
								name: "coordinatedOtherFuneralAllowance",
								valueG: coordinatedOtherFuneralAllowance,
							},
							{ name: "collectiveDeathPayout", valueG: collectiveDeathPayout },
						],
					},
				);
			}
		}

		return debugResult(0);
	},
	hasCoordinationInsurance: () => {
		const obj = get();

		if (!obj.insurances || obj.insurances.length === 0) {
			// console.error("No insurances found");
			return false;
		}

		return obj.insurances.some(
			(insurance) => insurance.type === "group-life-coordination",
		);
	},
	hasHealthInsurance: () => {
		const obj = get();

		if (!obj.insurances || obj.insurances.length === 0) {
			// console.error("No insurances found");
			return false;
		}

		return obj.insurances.some(
			(insurance) => insurance.type === "health-insurance",
		);
	},
	hasTravelInsurance: () => {
		const obj = get();

		if (!obj.insurances || obj.insurances.length === 0) {
			// console.error("No insurances found");
			return false;
		}

		return obj.insurances.some(
			(insurance) => insurance.type === "travel-insurance",
		);
	},
}));

function isIncludedInFilter(
	type: Insurance["type"],
	filter?: Insurance["type"][],
) {
	if (!filter || filter.length === 0) {
		return true; // No filter applied, include all types
	}

	// Check if the type is included in the filter. Return true if it is, false otherwise.
	return filter.includes(type);
}

const isFormulaDebugEnabled = () => {
	if (typeof window === "undefined") return false;
	return window.localStorage.getItem("formulas-debug") === "true";
};

const logFormulaDebug = (
	calculation: "invalidity" | "death",
	payload: {
		options: FormulasOptions;
		gValue: number;
		inputState: Record<string, unknown>;
		rows: DebugRow[];
		totals: Record<string, unknown>;
		formula?: {
			branch: string;
			terms: { name: string; valueG: number }[];
		};
	},
) => {
	if (!isFormulaDebugEnabled()) return;

	const calculationLabel =
		calculation === "death" ? "Doedsfall" : "Invaliditet";
	const causedByLabel =
		payload.options.causedBy === "accident" ? "Ulykke" : "Sykdom";
	const atLabel = payload.options.at === "work" ? "Arbeid" : "Fritid";
	const showLabel =
		payload.options.show === "full"
			? "Total"
			: payload.options.show === "employer"
				? "Arbeidsgiver"
				: "Lovpaalagt";
	const debugPath = `${calculationLabel} -> ${causedByLabel} -> ${atLabel} -> ${showLabel}`;

	const includedRows = payload.rows.filter((row) => row.included);
	const equation = includedRows
		.map((row) => `${row.component}(${row.valueG})`)
		.join(" + ");
	const formulaEquation = payload.formula?.terms
		.map((term) => `${term.name}(${term.valueG})`)
		.join(" + ");
	const formulaTotal =
		payload.formula?.terms.reduce((sum, term) => sum + term.valueG, 0) ?? null;

	console.groupCollapsed(
		`[formulas:${calculation}] ${debugPath} [${payload.options.currency}]`,
	);
	console.log("path", debugPath);
	console.log("options", payload.options);
	console.log("inputs", payload.inputState);
	console.log("gValue", payload.gValue);
	console.table(payload.rows);
	console.table(includedRows);
	console.log("equationG", equation || "Ingen inkluderte ledd");
	if (payload.formula) {
		console.log("formulaBranch", payload.formula.branch);
		console.table(payload.formula.terms);
		console.log("formulaEquationG", formulaEquation || "Ingen ledd");
		console.log("formulaTermsSumG", formulaTotal);
	}
	console.table([payload.totals]);
	console.groupEnd();
};

/** Prefer trygghetsforsikring over leisure-accident-death when both exist (unless filter is single-type). */
function resolveAccidentDeathOtherInsurance(
	insurances: Insurance[],
	filter?: Insurance["type"][],
): Insurance | undefined {
	const trygghets = insurances.find((i) => i.type === "trygghetsforsikring");
	const leisureDeath = insurances.find(
		(i) => i.type === "leisure-accident-death",
	);

	if (filter?.length === 1 && filter[0] === "leisure-accident-death") {
		return leisureDeath;
	}
	if (filter?.length === 1 && filter[0] === "trygghetsforsikring") {
		return trygghets;
	}

	if (trygghets && isIncludedInFilter("trygghetsforsikring", filter)) {
		return trygghets;
	}
	if (leisureDeath && isIncludedInFilter("leisure-accident-death", filter)) {
		return leisureDeath;
	}
	return undefined;
}

export default useFormulas;
